import {
    offset,
    flip,
    shift,
    size,
    useFloating,
    autoUpdate,
} from '@floating-ui/react-dom';
import {
    useState,
    MouseEvent,
    useCallback,
    ReactElement,
    useEffect,
    ChangeEvent,
} from 'react';
import { ChevronsUpDownIcon } from 'lucide-react';
import classnames from 'classnames';

import { SelectItemProps, SelectItemType } from './select-item';
import styles from './select.module.scss';

type Props = {
    children: ReactElement<SelectItemProps> | ReactElement<SelectItemProps>[];
    value?: string;
    items: SelectItemType[];
    placeholder?: string;
    searchable?: boolean;
    onSelect?: (item: SelectItemType) => void;
    onSearch?: (query: string) => void;
};

const Select = ({
    children,
    value,
    items,
    placeholder,
    searchable,
    onSelect,
    onSearch,
}: Props) => {
    const [isOpen, setIsOpen] = useState(false);
    const [selectedItemLabel, setSelectedItemLabel] = useState<string>();

    const { refs, floatingStyles, update, elements } = useFloating({
        middleware: [
            flip(),
            offset(4),
            shift(),
            size({
                apply({ rects, elements }) {
                    Object.assign(elements.floating.style, {
                        width: `${rects.reference.width}px`,
                    });
                },
            }),
        ],
    });

    useEffect(() => {
        if (isOpen && elements.reference && elements.floating) {
            return autoUpdate(elements.reference, elements.floating, update);
        }
    }, [isOpen, elements, update]);

    useEffect(() => {
        const selected = items.find((item) => item.selected);
        if (!selected) return;
        selectHtmlItem(selected.id.toString());
        setSelectedItemLabel(selected.label);
    }, [items]);

    useEffect(() => {
        if (!value || items.length === 0) {
            deselectItems();
            setSelectedItemLabel(undefined);
            return;
        }
        const selected = items.find((item) => item.id.toString() === value);
        if (!selected) return;
        selectHtmlItem(value);
        setSelectedItemLabel(selected.label);
    }, [items, value]);

    const toggleOpen = (ev: MouseEvent) => {
        ev.preventDefault();
        setIsOpen(!isOpen);
    };

    const selectHtmlItem = useCallback((key: string) => {
        const options =
            refs.floating.current?.querySelectorAll('[role=option]');
        if (!options) return;
        Array.from(options).map((opt: any) => {
            if (opt.dataset.key === key) {
                opt.dataset.selected = 'true';
            } else {
                opt.dataset.selected = 'false';
            }
        });
    }, []);

    const deselectItems = useCallback(() => {
        const options =
            refs.floating.current?.querySelectorAll('[role=option]');
        if (!options) return;
        Array.from(options).map((opt: any) => {
            opt.dataset.selected = 'false';
        });
    }, []);

    const handleSelection = useCallback(
        (ev: MouseEvent) => {
            const option = (ev.target as HTMLElement).closest(
                '[role=option]'
            ) as HTMLLIElement;
            const key = (option as HTMLLIElement)?.dataset.key;
            if (!key) return;

            const selectedItem = items.find(
                (item) => item.id.toString() === key
            );
            if (!selectedItem) return;

            setSelectedItemLabel(selectedItem.label);
            setIsOpen(false);
            onSelect?.(selectedItem);

            // set the selected item and clear the rest
            selectHtmlItem(key);
        },
        [items, onSelect]
    );

    const handleSearch = (ev: ChangeEvent) => {
        const query = (ev.target as HTMLInputElement).value.trim();
        onSearch?.(query);
    };

    return (
        <>
            <button
                role="selected-box"
                className={styles.selectedBox}
                ref={refs.setReference}
                onClick={toggleOpen}
            >
                <span>{selectedItemLabel ?? placeholder}</span>
                <ChevronsUpDownIcon size={18} color="#515257" strokeWidth={2} />
            </button>
            <ul
                role="option-list"
                ref={refs.setFloating}
                style={{ ...floatingStyles }}
                className={classnames(styles.floatingList, {
                    [styles.shown]: isOpen,
                })}
                onClick={handleSelection}
            >
                {searchable && (
                    <input
                        className={styles.selectInput}
                        placeholder="Buscar..."
                        onChange={handleSearch}
                    />
                )}
                <div className={styles.optionList}>{children}</div>
            </ul>
        </>
    );
};

export { Select };
